import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/arina';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [activeCategory, setActiveCategory] = useState(slug || 'profile');

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name} - 桥本有菜官方资料站`,
    description: currentCategory?.description,
    url: `https://hashimoto-arina.com/category/${activeCategory}`,
    hasPart: filteredArticles.map((a) => ({
      '@type': 'Article',
      headline: a.title,
      url: `https://hashimoto-arina.com/article/${a.slug}`,
      datePublished: a.date,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />
        <main className="pt-24 pb-20">
          <div className="w-full max-w-6xl mx-auto px-6">
            <Breadcrumb items={getCategoryBreadcrumb(slug || '')} />

            <div className="mt-6 mb-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                {currentCategory?.name}
              </h1>
              <p className="text-base text-foreground-500">{currentCategory?.description}</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              <aside className="lg:w-56 shrink-0">
                <div className="lg:sticky lg:top-24 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.slug)}
                      className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeCategory === cat.slug
                          ? 'bg-primary-50 text-primary-700 border border-primary-200'
                          : 'text-foreground-600 hover:bg-background-100 border border-transparent'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                {filteredArticles.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-foreground-500">该分类暂无文章</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {filteredArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                      >
                        <div className="relative w-full h-44 overflow-hidden">
                          <img
                            src={article.coverImage}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-5">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs text-foreground-400">{article.date}</span>
                          </div>
                          <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-500 line-clamp-2">{article.excerpt}</p>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {article.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}